import { useState } from "react";
import ReactMarkdown from "react-markdown";
import { Check, Copy } from "lucide-react";
import { Tip } from "@/components/Tip";

/**
 * Shared Markdown renderer (extracted from ChatThread).
 *
 * SECURITY: renders Markdown ONLY via react-markdown (no rehype-raw), so raw
 * HTML/JS from the source is NOT executed — it is emitted as inert text.
 * This is the single Markdown pipeline for the app: chat messages AND the
 * Project Files .md preview both use it. Do not add a second renderer.
 */

// Copy a string to the clipboard (with a textarea fallback).
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    try {
      document.execCommand("copy");
      document.body.removeChild(ta);
      return true;
    } catch {
      document.body.removeChild(ta);
      return false;
    }
  }
}

// Extract plain text from a React node (children of pre -> code).
function extractText(node: unknown): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractText).join("");
  const props = (node as { props?: { children?: unknown } }).props;
  return props ? extractText(props.children) : "";
}

// Code block (pre) with a copy button.
export function CodeBlock({ children }: { children?: unknown }) {
  const [copied, setCopied] = useState(false);
  const codeText = extractText(children).replace(/\n$/, "");
  return (
    <div className="code-block-wrap relative">
      <Tip label="Копировать код" side="bottom" className="absolute top-2 right-2 z-10">
        <button
          onClick={async () => {
            if (await copyText(codeText)) {
              setCopied(true);
              setTimeout(() => setCopied(false), 1600);
            }
          }}
          className="code-copy-btn p-1.5 rounded-md transition-colors"
          aria-label="Копировать код"
        >
          {copied ? <Check size={13} className="text-accent" /> : <Copy size={13} />}
        </button>
      </Tip>
      <pre>{children as unknown as string}</pre>
    </div>
  );
}

export function MarkdownBlock({
  content,
  onCitationClick,
}: {
  content: string;
  /**
   * Sources UX: when provided, numbered citation anchors ``[n](#cite-n)``
   * (produced by mapCitationTokensAsLinks) render as small buttons that
   * open the Sources panel on the matching row instead of navigating.
   */
  onCitationClick?: (n: number) => void;
}) {
  const components: Record<string, unknown> = { pre: CodeBlock };
  if (onCitationClick) {
    components.a = ({
      href,
      children,
    }: {
      href?: string;
      children?: unknown;
    }) => {
      const m = /^#cite-(\d+)$/.exec(href || "");
      if (m) {
        const n = Number(m[1]);
        return (
          <button
            type="button"
            data-testid={`citation-link-${n}`}
            aria-label={`Открыть источник ${n}`}
            onClick={() => onCitationClick(n)}
            className="inline-flex items-center justify-center min-w-[1.35em] px-0.5 mx-0.5 -translate-y-[2px] rounded text-[0.75em] font-medium align-baseline text-accent bg-accent/10 hover:bg-accent/20 transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-accent"
          >
            {children as never}
          </button>
        );
      }
      return (
        <a href={href} target="_blank" rel="noopener noreferrer">
          {children as never}
        </a>
      );
    };
  }
  return (
    <div className="rounded-lg bg-elevated border border-strongline p-4 my-2">
      <div className="prose prose-p:leading-relaxed max-w-full overflow-x-auto prose-headings:text-primary prose-strong:text-primary">
        <ReactMarkdown components={components as never}>{content || ""}</ReactMarkdown>
      </div>
    </div>
  );
}
